/**
 * Where the Playground's first buffer comes from: `PG-UX05`, `docs/24` §9.1.
 *
 * One precedence order, stated once and tested once, so the client's effects
 * only ask "which target am I on" and "have I applied it":
 *
 *   1. an explicit target: a shared snippet payload, or a saved snippet named
 *      by `?snippet=` (a value that is not an ObjectId is ignored, not an error);
 *   2. a `?code=` starter from a course lesson, on the route's language;
 *   3. the per-language local draft the student left behind;
 *   4. the language's default template.
 *
 * A target has a key. The client applies a key once and remembers it, which is
 * what stops a consumed `?code=` or `?snippet=` (the param is stripped after
 * use) from re-running the startup and replacing what the student has typed
 * since. Back and forward between language routes change the key and are
 * meant to re-run it.
 *
 * Pure functions, so the whole order is a table of unit tests rather than a
 * sequence of effects nobody can read in one sitting.
 */
import { SNIPPET_OBJECT_ID_REGEX } from "../types";
import type { PlaygroundDraft } from "./playground-draft-storage";

export type StartupTarget =
  | { kind: "shared" }
  | { kind: "snippet"; snippetId: string }
  | { kind: "language"; languageId: string };

export function resolveStartupTarget(input: {
  hasSharedPayload: boolean;
  snippetId: string | null | undefined;
  routeLanguageId: string;
}): StartupTarget {
  if (input.hasSharedPayload) return { kind: "shared" };
  const snippetId = input.snippetId?.trim() ?? "";
  if (snippetId && SNIPPET_OBJECT_ID_REGEX.test(snippetId)) {
    return { kind: "snippet", snippetId };
  }
  return { kind: "language", languageId: input.routeLanguageId };
}

/** Stable identity for "the startup this buffer was set up for". */
export function startupTargetKey(target: StartupTarget): string {
  switch (target.kind) {
    case "shared":
      return "shared";
    case "snippet":
      return `snippet:${target.snippetId}`;
    case "language":
      return `language:${target.languageId}`;
  }
}

export type ScratchStartupSource =
  | { source: "url-code"; sourceCode: string; stdin: string }
  | { source: "draft"; sourceCode: string; stdin: string }
  | { source: "default"; sourceCode: string; stdin: "" };

/**
 * Steps 2 to 4 of the order, for a `language` target. `urlStdin` is the input
 * a `?code=` link carries beside its program (`?stdin=`, `RV-13`): the Learn
 * reader's "Open in the Playground" sends an example's input with its code. It
 * only ever travels with `?code=`; on its own it is ignored.
 */
export function resolveScratchSource(input: {
  urlCode: string | null;
  urlStdin?: string | null;
  draft: PlaygroundDraft | null;
  defaultCode: string;
}): ScratchStartupSource {
  if (input.urlCode && input.urlCode.trim()) {
    return { source: "url-code", sourceCode: input.urlCode, stdin: input.urlStdin ?? "" };
  }
  if (input.draft && input.draft.sourceCode.length > 0) {
    return { source: "draft", sourceCode: input.draft.sourceCode, stdin: input.draft.stdin };
  }
  return { source: "default", sourceCode: input.defaultCode, stdin: "" };
}

export type SnippetHydrationDecision = "hydrate" | "skip" | "confirm";

/**
 * Whether a snippet payload that just arrived may replace the buffer.
 *
 * `skip` when this id has already been applied: a refetch after a save, or a
 * window-focus refetch, must never overwrite what the student typed since.
 * `confirm` when the buffer holds unsaved edits and the student has not already
 * approved opening this exact snippet (the quick-access picker approves before
 * navigating). Otherwise `hydrate`.
 */
export function decideSnippetHydration(input: {
  loadedSnippetId: string;
  hydratedSnippetId: string | null;
  hasUnsavedChanges: boolean;
  approvedSnippetId: string | null;
}): SnippetHydrationDecision {
  if (input.loadedSnippetId === input.hydratedSnippetId) return "skip";
  if (input.hasUnsavedChanges && input.approvedSnippetId !== input.loadedSnippetId) {
    return "confirm";
  }
  return "hydrate";
}
